// src/components/common/ExportProgressModal/ExportProgressModal.jsx
import React from 'react';
import { Download, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './ExportProgressModal.module.css';

const ExportProgressModal = ({
  isOpen, onClose, onCancel, onMinimize, status, error, totalRecords = 0, fetchedRecords = 0, title = 'Exporting Orders', runningText, doneText,
}) => {
  const { t } = useLocalization();

  if (!isOpen) return null;

  const pct       = totalRecords > 0 ? Math.round((fetchedRecords / totalRecords) * 100) : 0;
  const isDone    = status === 'done';
  const isFailed  = status === 'error';
  const isRunning = status === 'running';
  const runningDismiss = onMinimize || onCancel;

  return (
    <div className={styles.exportModalOverlay}>
      <div className={styles.exportModal}>
        <div className={styles.exportModalHeader}>
          <div className={styles.exportModalTitle}>
            <Download size={20} />
            <span>{title}</span>
          </div>
          {(isDone || isFailed) && (
            <button onClick={onClose} className={styles.exportModalClose}><X size={18} /></button>
          )}
          {isRunning && runningDismiss && (
            <button onClick={runningDismiss} className={styles.exportModalClose} title={onMinimize ? t('common.close') : t('common.cancel')}>
              <X size={18} />
            </button>
          )}
        </div>

        <div className={styles.exportModalBody}>
          {isRunning && <div className={styles.exportSpinner}><Loader2 size={32} className={styles.spinnerIcon} /></div>}
          {isDone    && <div className={styles.exportSuccessIcon}><CheckCircle size={32} /></div>}
          {isFailed  && <div className={styles.exportErrorIcon}><AlertCircle size={32} /></div>}

          <p className={styles.exportStatusText}>
            {isFailed
              ? error || 'Export failed. Please try again.'
              : isDone
              ? doneText || 'Export complete! Your file has been downloaded.'
              : runningText || `Fetching records… ${fetchedRecords.toLocaleString()} of ${totalRecords > 0 ? totalRecords.toLocaleString() : '?'}`}
          </p>

          {!isFailed && totalRecords > 0 && (
            <>
              <div className={styles.exportProgressBar}>
                <div
                  className={`${styles.exportProgressFill} ${isDone ? styles.exportProgressDone : ''}`}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
              <p className={styles.exportProgressPct}>{isDone ? 100 : pct}%</p>
            </>
          )}

          {!isFailed && totalRecords === 0 && isRunning && (
            <div className={styles.exportProgressBar}>
              <div className={styles.exportProgressIndeterminate} />
            </div>
          )}
        </div>

        {(isDone || isFailed) && (
          <div className={styles.exportModalFooter}>
            <button onClick={onClose} className={styles.closeButton}>{t('common.close') || 'Close'}</button>
          </div>
        )}

        {isRunning && (onCancel || onMinimize) && (
          <div className={styles.exportModalFooter}>
            {onMinimize && (
              <button onClick={onMinimize} className={styles.closeButton}>{t('common.close') || 'Close'}</button>
            )}
            {onCancel && (
              <button onClick={onCancel} className={styles.cancelButton}>{t('common.cancel') || 'Cancel'}</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportProgressModal;
